import axios from 'axios';

/**
 * Axios Configuration
 * Centralized configuration for API requests
 * 
 * Environment Variables:
 * - VITE_API_BASE_URL: Base URL for the backend API (default: http://localhost:5000)
 * 
 * Usage:
 *   import api from '@/config/axios';
 *   const response = await api.get('/api/users');
 */

// Get API base URL from environment variable
// In Vite, environment variables must be prefixed with VITE_ to be exposed
// If VITE_API_BASE_URL is empty string, use it (for nginx proxy with relative paths)
// Otherwise use the provided URL or fallback to localhost
const envBaseUrl = import.meta.env.VITE_API_BASE_URL;
const API_BASE_URL = envBaseUrl !== undefined ? envBaseUrl : 'http://localhost:5001';

// Debug: Log the environment variable value
console.log('🔧 Environment Variables:', {
  VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
  MODE: import.meta.env.MODE,
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD,
  allEnvVars: import.meta.env
});

// Always use the configured base URL from .env
const baseURL = API_BASE_URL;

console.log('🌐 Axios Base URL configured as:', baseURL);

// Set axios defaults for backward compatibility (for files still using raw axios)
// This ensures any remaining axios calls work correctly in production
axios.defaults.baseURL = baseURL;
axios.defaults.withCredentials = true;

// Create axios instance with default configuration
const api = axios.create({
  baseURL: baseURL,
  withCredentials: true, // Important for cookies/sessions
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // You can add auth tokens here if needed
    // const token = localStorage.getItem('token');
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      switch (error.response.status) {
        case 401:
          // Unauthorized - redirect to login
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          break;
        case 403:
          // Forbidden
          console.error('Access forbidden');
          break;
        case 404:
          // Not found
          console.error('Resource not found');
          break;
        case 500:
          // Server error
          console.error('Server error');
          break;
        default:
          break;
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network error - no response from server');
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export default api;
